import { z } from 'zod';
import { TriageConfigSchema } from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

/**
 * Wire shape for the per-org triage config. Reuses the domain
 * `TriageConfigSchema` core (`enabled`, `prompt`, `updatedAt`,
 * `updatedByUserId`) and adds the optimistic-locking counter `_v` —
 * read on GET, echoed back as `expectedV` on PUT. Mirrors
 * `BoardConfigResponseSchema` in `board/board_repository.ts`.
 */
const TriageConfigResponseSchema = TriageConfigSchema.extend({
  _v: z.number().int().min(0),
});

export type TriageConfigResponse = z.infer<typeof TriageConfigResponseSchema>;

export interface UpdateTriageConfigInput {
  expectedV: number;
  enabled: boolean;
  prompt: string;
}

const RegeneratePromptResponseSchema = z.object({ prompt: z.string() });

interface TriageRepository {
  loadTriageConfig: (orgId: string) => Promise<TriageConfigResponse>;
  updateTriageConfig: (
    orgId: string,
    payload: UpdateTriageConfigInput,
  ) => Promise<TriageConfigResponse>;
  /** Asks the backend (LLM) to suggest a triage prompt from the org's squads. */
  regeneratePrompt: (orgId: string) => Promise<{ prompt: string }>;
}

function orgPath(orgId: string): string {
  return `/orgs/${encodeURIComponent(orgId)}/triage-config`;
}

export function createTriageRepository(http: HttpService): TriageRepository {
  return {
    loadTriageConfig: (orgId) => http.get(orgPath(orgId), { schema: TriageConfigResponseSchema }),
    updateTriageConfig: (orgId, payload) =>
      http.put(orgPath(orgId), payload, { schema: TriageConfigResponseSchema }),
    regeneratePrompt: (orgId) =>
      http.post(
        `${orgPath(orgId)}/regenerate-prompt`,
        {},
        { schema: RegeneratePromptResponseSchema },
      ),
  };
}
